import classnames from 'classnames';
import type {KeyboardEvent, MouseEvent, ReactNode} from 'react';
import React, {useEffect, useRef} from 'react';
import {ConfirmationModal} from '@albert-io/atomic';

import AnnotationEditor from './AnnotationEditor';
import {useIndicatorRegion} from './IndicatorRegionStore';
import {
  annotationIdOfMark,
  findMarkFocusStop,
  markElementOf,
  markOpenedByControl
} from './annotationMarkElements';
import {ANNOTATION_REGION_PROPS} from './annotationTextProjection';
import {annotationAnchorAtPoint} from './annotationUtils';
import {useAnnotationGesture} from './useAnnotationGesture';
import {useAnnotations} from './useAnnotations';
import {useCanViewAnnotations} from './useCanViewAnnotations';
import {useMarkHoverPaint} from './useMarkHoverPaint';

import './annotations-cursor.scss';
import './annotations-highlights.scss';

interface AnnotatableContentProps {
  regionId: string | null;
  /** Language of the rendered text ('es' when translated); null = original. */
  lang?: string | null;
  /**
   * Styles the region wrapper div (e.g. `contents` where it must stay
   * layout-transparent). Unavailable/flag-off renders bare children without it.
   */
  className?: string;
  /** False where the region's notes surface elsewhere, as a passage's do. */
  showNoteIndicators?: boolean;
  /** True where the region's DOM can move (a reordered chip), so it repaints where it lands. */
  repaintOnMove?: boolean;
  children: ReactNode;
}

/**
 * Wraps children so selections inside them become annotations (see
 * `useAnnotations`). Anchors index the region's normalized visible text
 * (annotationTextProjection.ts), not the source markdown.
 */
export default function AnnotatableContent({
  regionId,
  lang = null,
  className,
  showNoteIndicators = true,
  repaintOnMove = false,
  children
}: AnnotatableContentProps) {
  const canViewAnnotations = useCanViewAnnotations();

  if (!canViewAnnotations || !regionId) {
    return <>{children}</>;
  }

  return (
    <AnnotatableRegion
      regionId={regionId}
      lang={lang}
      className={className}
      showNoteIndicators={showNoteIndicators}
      repaintOnMove={repaintOnMove}
    >
      {children}
    </AnnotatableRegion>
  );
}

function AnnotatableRegion({
  regionId,
  lang,
  className,
  showNoteIndicators,
  repaintOnMove,
  children
}: {
  regionId: string;
  lang: string | null;
  className?: string;
  showNoteIndicators: boolean;
  repaintOnMove: boolean;
  children: ReactNode;
}) {
  const {
    annotations,
    captureAnnotation,
    getAnnotationAtPoint,
    containerRef,
    container,
    getProjection,
    isMergeConfirmPending,
    isMergeNoteTooLong,
    confirmPendingMerge,
    cancelPendingMerge
  } = useAnnotations(regionId, lang, {repaintOnMove});
  const {activeAnnotation, canEdit, handleMouseUp, openEditor, closeEditor} = useAnnotationGesture({
    regionId,
    capture: (event) => captureAnnotation(event.clientX, event.clientY),
    // A plain click on an existing annotation opens its style editor.
    resolveAtPoint: (event) => getAnnotationAtPoint(event.clientX, event.clientY)
  });
  useMarkHoverPaint({regionId, container, annotations, getProjection, canEdit});
  useIndicatorRegion(showNoteIndicators ? regionId : null, container, getProjection, lang);
  const keyboardEditIdRef = useRef<string | null>(null);

  // Capture phase: React dispatches it before the frozen question area's shield runs.
  const handleKeyDownCapture = (event: KeyboardEvent<HTMLDivElement>) => {
    const mark = container && markElementOf(event.target, container);
    if (!canEdit || !mark || (event.key !== 'Enter' && event.key !== ' ')) {
      return;
    }
    const id = annotationIdOfMark(mark, container);
    if (!id) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    keyboardEditIdRef.current = id;
    const box = mark.getBoundingClientRect();
    openEditor(annotationAnchorAtPoint(box.left, box.bottom, id));
  };

  // Screen readers in browse mode activate the focus stop with a click, not a keydown.
  const handleClickCapture = (event: MouseEvent<HTMLDivElement>) => {
    const mark = container && markElementOf(event.target, container);
    const id =
      mark && event.detail === 0 && mark.tabIndex === 0 && annotationIdOfMark(mark, container);
    if (!canEdit || !mark || !id) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    keyboardEditIdRef.current = id;
    const box = mark.getBoundingClientRect();
    openEditor(annotationAnchorAtPoint(box.left, box.bottom, id));
  };

  // A cell or chip holding the mark is already a control, so the mark has no focus stop.
  // Document capture: the key reaches here before the chip's drag handlers or the shield.
  useEffect(() => {
    if (!canEdit || !container) {
      return undefined;
    }
    const openFromControl = (event: Event) => {
      const mark = markOpenedByControl(event.target, container);
      const id = mark && annotationIdOfMark(mark, container);
      if (!mark || !id) {
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      const box = mark.getBoundingClientRect();
      openEditor(annotationAnchorAtPoint(box.left, box.bottom, id));
    };
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        openFromControl(event);
      }
    };
    const handleClick = (event: globalThis.MouseEvent) => {
      if (event.detail === 0) {
        openFromControl(event);
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('click', handleClick, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('click', handleClick, true);
    };
  }, [canEdit, container, openEditor]);

  useEffect(() => {
    const id = keyboardEditIdRef.current;
    if (activeAnnotation || !id || !container) {
      return;
    }
    keyboardEditIdRef.current = null;
    findMarkFocusStop(container, id)?.focus({preventScroll: true});
  }, [activeAnnotation, container]);

  return (
    <>
      <div
        {...ANNOTATION_REGION_PROPS}
        ref={containerRef}
        className={canEdit ? classnames(className, 'cursor-highlighter') : className}
        onMouseUp={handleMouseUp}
        onKeyDownCapture={handleKeyDownCapture}
        onClickCapture={handleClickCapture}
      >
        {children}
      </div>
      {isMergeConfirmPending && (
        // Placeholder presentation: design wants this anchored to the selection
        // like the style popover, with final copy TBD (#19010 Slack thread).
        <ConfirmationModal
          body={
            isMergeNoteTooLong
              ? 'These notes are too long to combine. Shorten one before merging these annotations.'
              : 'Do you want to merge these annotations?'
          }
          badgeColor={isMergeNoteTooLong ? 'warning' : undefined}
          confirmText='Merge'
          confirmDisabled={isMergeNoteTooLong}
          cancelText={isMergeNoteTooLong ? 'Close' : 'Cancel'}
          onConfirm={() => openEditor(confirmPendingMerge())}
          onCancel={() => {
            closeEditor();
            cancelPendingMerge();
          }}
        />
      )}
      {!isMergeConfirmPending && activeAnnotation && (
        <AnnotationEditor
          regionId={regionId}
          activeAnnotation={activeAnnotation}
          onClose={closeEditor}
        />
      )}
    </>
  );
}
